import { marketTypes } from 'utils/constants/sportsbook/data-fields';
import { WINNER_MARKETS } from 'utils/constants/sportsbook/grouped-market-names';
import { NOT_REAL_SPORTS } from 'utils/constants/sportsbook/sportsbook-data-hierarchy';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { getLivePreMatchSportTypes } from 'utils/sportsbook/sport-data';
import { EPageTypes } from 'interfaces/sportsbook';

type TWhereOptions = {
  pageType: EPageTypes;
  sportAlias: string | { '@in': string[] };
  regionAlias: string;
  competitionId: number;
  marketType: string | null;
  timestamp: Record<string, number | undefined>;
  favoriteTeamId: number | null;
  tournamentSportIds: (string | number | null)[] | null;
  isUsaLayout?: boolean;
};

type TWhereResult = {
  sport?: {
    alias?: string;
    type?: Record<string, any>;
    id?: {
      '@in': (number | string | null)[];
    };
  };
  region?: {
    alias: string;
  };
  competition?: {
    id: number;
  };
  game: Record<string, any>;
  market: Record<string, any>;
};

type TWhatOptions = {
  pageType: EPageTypes;
};

type TWhatResult = Record<string, string[] | string[][]>;

const LIVE_GAME_WHAT = [
  'id',
  'type',
  'team1_name',
  'team2_name',
  'team1_id',
  'team2_id',
  'order',
  'start_ts',
  'markets_count',
  'is_blocked',
  'info',
  'tv_type',
  'exclude_ids',
  'show_type',
  'text_info',
  'add_info_name',
  'tv_info',
  'is_stat_available',
  'video_id',
  'video_id2',
  'video_id3',
  'sportcast_id',
  'match_length',
  'live_events',
  'game_number'
];

const PREMATCH_GAME_WHAT = [
  'id',
  'team1_name',
  'team2_name',
  'team1_id',
  'team2_id',
  'order',
  'start_ts',
  'markets_count',
  'is_blocked',
  'show_type',
  'sportcast_id',
  'is_stat_available',
  'game_number'
];

export const getWhat = ({ pageType }: TWhatOptions): TWhatResult => ({
  sport: ['id', 'name', 'alias'],
  region: ['id', 'name', 'alias', 'order'],
  competition: ['id', 'name', 'order'],
  game: [pageType === EPageTypes.live ? LIVE_GAME_WHAT : PREMATCH_GAME_WHAT],
  market: [
    'name',
    'order',
    'type',
    'id',
    'base',
    'col_count',
    'main_order',
    'express_id',
    ...(pageType === EPageTypes.prematch ? ['prematch_express_id'] : [])
  ],
  event: ['id', 'name', 'price', 'base', 'order', 'type_1']
});

export const getWhere = ({
  pageType,
  sportAlias,
  regionAlias,
  competitionId,
  marketType,
  timestamp,
  favoriteTeamId,
  tournamentSportIds,
  isUsaLayout
}: TWhereOptions): TWhereResult => {
  if (SpringConfigs.MOCKED_DATA) {
    return {
      sport: { alias: 'Soccer' }
    } as TWhereResult;
  }

  const isLive = pageType === EPageTypes.live;

  const result: TWhereResult = {
    game: isLive
      ? { type: 1 }
      : {
          '@or': favoriteTeamId
            ? [
                {
                  team1_id: favoriteTeamId
                },
                {
                  team2_id: favoriteTeamId
                }
              ]
            : [{ type: { '@in': [0, 2] } }, { visible_in_prematch: 1 }]
        },
    market: marketType
      ? { type: marketType }
      : {
          '@or': [
            {
              type: {
                '@in': isLive ? marketTypes : WINNER_MARKETS
              }
            },
            { display_key: 'OUTRIGHT' }
          ]
        }
  };

  if (isUsaLayout) {
    result.market = {
      display_key: {
        '@in': ['WINNER', 'HANDICAP', 'TOTALS']
      },
      display_sub_key: {
        '@in': ['MATCH']
      }
    };
  }

  if (!NOT_REAL_SPORTS.includes(sportAlias as string)) {
    result.sport = {
      alias: sportAlias as string,
      type: {
        '@in': getLivePreMatchSportTypes(pageType)
      }
    };
  }

  if (pageType === EPageTypes.prematch) {
    if (regionAlias) {
      result.region = {
        alias: regionAlias
      };
    }

    if (competitionId) {
      result.competition = {
        id: competitionId
      };
    }

    if (
      Object.keys(timestamp || {}).length &&
      !NOT_REAL_SPORTS.includes(sportAlias as string)
    ) {
      result.game.start_ts = timestamp;
    }

    if (tournamentSportIds) {
      result.sport = {
        ...(result.sport || {}),
        id: {
          '@in': tournamentSportIds
        }
      };
    }
  }

  return result;
};
